import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-media-query';
import { formatMomentDay } from '@/modules/service-desk/shared/application-utils';
import {
  actionsColumn,
  appliedDateColumn,
  statusColumn,
  textColumn,
  withCard,
} from '@/modules/service-desk/shared/components/column-helpers';
import {
  availableByodActions,
  canEditByod,
  isAppliedWithinWindow,
  readAccountNumber,
} from '../../utils/byod-rules';
import { ByodApplication, ByodStatus } from '../../types/byod.types';

type Translate = (key: string) => string;

/** Existing UILM label for the action that moves an application to `status`. */
const ACTION_LABEL_KEY: Record<ByodStatus, string> = {
  Approved: 'APPROVE',
  Rejected: 'REJECT',
  Pending: 'REVERT_TO_PENDING',
};

const STATUSES: ByodStatus[] = ['Pending', 'Approved', 'Rejected'];

const mobileColumn = (t: Translate) =>
  textColumn<ByodApplication>(t, 'MobileNumber', 'MOBILE_NUMBER', (row) => row.MobileNumber);

/** The account is inside `BankInfoJson`, so the column is neither sorted nor filtered. */
const bankAccountColumn = (t: Translate) =>
  textColumn<ByodApplication>(
    t,
    'BankAccount',
    'BANK_ACCOUNT',
    (row) => readAccountNumber(row.BankInfoJson),
    {
      filter: false,
      sortable: false,
    }
  );

/** Old applications kept their department as free text (`BusinessUnitName`). */
const departmentColumn = (t: Translate) =>
  textColumn<ByodApplication>(
    t,
    'RequestDepartmentName',
    'DEPARTMENT',
    (row) => row.RequestDepartmentName || row.BusinessUnitName
  );

/**
 * Applied date, highlighted on pending rows: applications made after the cutoff day are processed
 * next month, so reviewers see at a glance which ones fall in this month's window.
 */
const byodAppliedDateColumn = (t: Translate): ColumnDef<ByodApplication> =>
  appliedDateColumn<ByodApplication>(t, 'REQUEST_DATE', ({ row }) => {
    const application = row.original;
    const pending = application.Status === 'Pending';
    const inWindow = isAppliedWithinWindow(application.AppliedDateUtc, application.Timezone);
    return (
      <span
        className={cn(
          pending && inWindow && 'text-success',
          pending && !inWindow && 'text-warning'
        )}
      >
        {formatMomentDay(application.AppliedDateUtc, application.Timezone)}
      </span>
    );
  });

/** The name with the email under it; on a phone card the name alone (the email has its own value). */
const ApplicantNameCell = ({ application }: { application: ByodApplication }) =>
  useIsMobile() ? (
    <>{application.FullName || '-'}</>
  ) : (
    <UserNameCell name={application.FullName} email={application.EmailAddress} />
  );

interface MyColumnsProps {
  t: Translate;
  onViewSpecs: (application: ByodApplication) => void;
  onEdit: (application: ByodApplication) => void;
  onWithdraw: (application: ByodApplication) => void;
  canApply: boolean;
  canWithdraw: boolean;
}

export const createMyByodColumns = ({
  t,
  onViewSpecs,
  onEdit,
  onWithdraw,
  canApply,
  canWithdraw,
}: MyColumnsProps): ColumnDef<ByodApplication>[] => [
  // Card: the device model is the title and the actions menu the top right; the body is
  // Request Date | Status (the rest is in the table and the specs dialog).
  withCard(byodAppliedDateColumn(t), { mobile: 'body', mobileOrder: 0 }),
  withCard(
    textColumn<ByodApplication>(t, 'DeviceModel', 'DEVICE_MODEL', (row) => row.DeviceModel),
    { mobile: 'title' }
  ),
  withCard(departmentColumn(t), { mobile: 'hidden' }),
  withCard(mobileColumn(t), { mobile: 'hidden' }),
  withCard(bankAccountColumn(t), { mobile: 'hidden' }),
  withCard(statusColumn<ByodApplication>(t, STATUSES), { mobile: 'body', mobileOrder: 1 }),
  actionsColumn<ByodApplication>(t, (application) => [
    { label: t('VIEW_SPECS'), onSelect: () => onViewSpecs(application) },
    {
      label: t('EDIT'),
      onSelect: () => onEdit(application),
      hidden: !canApply || !canEditByod(application),
    },
    {
      label: t('WITHDRAW_BYOD'),
      onSelect: () => onWithdraw(application),
      hidden: !canWithdraw || application.Status !== 'Pending',
      destructive: true,
    },
  ]),
];

interface RequestColumnsProps {
  t: Translate;
  canReview: boolean;
  onViewSpecs: (application: ByodApplication) => void;
  onChangeStatus: (application: ByodApplication, status: ByodStatus) => void;
}

export const createByodRequestColumns = ({
  t,
  canReview,
  onViewSpecs,
  onChangeStatus,
}: RequestColumnsProps): ColumnDef<ByodApplication>[] => [
  // Card: the request date is the title and the actions menu the top right. The body runs
  // Name | Status, Employee ID | Email, Designation | Department, Mobile | Bank Account.
  withCard(byodAppliedDateColumn(t), { mobile: 'title' }),
  {
    id: 'FullName',
    meta: { filter: { type: 'text' }, mobile: 'body', mobileOrder: 0 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
    accessorFn: (row) => row.FullName || '-',
    cell: ({ row }) => <ApplicantNameCell application={row.original} />,
  },
  withCard(
    textColumn<ByodApplication>(t, 'EmployeeId', 'EMPLOYEE_ID', (row) => row.EmployeeId),
    { mobile: 'body', mobileOrder: 2 }
  ),
  // On the card only: the table shows the email under the name.
  withCard(
    textColumn<ByodApplication>(t, 'EmailAddress', 'EMAIL', (row) => row.EmailAddress),
    { mobile: 'body', mobileOrder: 3, cardOnly: true }
  ),
  withCard(
    textColumn<ByodApplication>(t, 'Designation', 'DESIGNATION', (row) => row.Designation),
    { mobile: 'body', mobileOrder: 4 }
  ),
  withCard(departmentColumn(t), { mobile: 'body', mobileOrder: 5 }),
  withCard(mobileColumn(t), { mobile: 'body', mobileOrder: 6 }),
  withCard(bankAccountColumn(t), { mobile: 'body', mobileOrder: 7 }),
  withCard(statusColumn<ByodApplication>(t, STATUSES), { mobile: 'body', mobileOrder: 1 }),
  actionsColumn<ByodApplication>(t, (application) => [
    { label: t('VIEW_SPECS'), onSelect: () => onViewSpecs(application) },
    ...availableByodActions(application.Status).map((status) => ({
      label: t(ACTION_LABEL_KEY[status]),
      onSelect: () => onChangeStatus(application, status),
      hidden: !canReview,
      destructive: status === 'Rejected',
    })),
  ]),
];
